/**
 * The stage bench's suggestion for one job (and seat), at the top of its rules
 * modal: every benched model's clean runs, harm, tokens and time, the pick
 * (cheapest of those tied for best), whether this policy already runs it, and
 * every metric the job reports — with the model that led each one, since the
 * cheapest or most precise model is worth seeing even when another wins.
 *
 * Applying is two plain rule edits — allow the pick, and for "use only",
 * remove the rest — so it goes through the same DRAFT-only path as typing a
 * pattern by hand. The ranking itself is the backend's (`bench/advice.py`),
 * which the bench draft shares, so the two can never disagree.
 */
import { useState } from 'react';
import type { BenchAdvice, BenchMetricDef, StageModelRule } from '../../types';
import {
  adviceHeadline,
  adviceSummary,
  formatMetric,
  rowRuleAction,
  splitAdviceColumns,
  standoutsByModel,
} from '../../pipeline/houseRules';

interface Props {
  advice: BenchAdvice | null;
  readOnly: boolean;
  busy: boolean;
  onAllow: (model: string) => void;
  onUseOnly: (model: string) => void;
  /** This job's OWN rules (narrowed to the seat), so a recommended row can
   * offer Allow or Remove against them. */
  rules: StageModelRule[];
  onRemove: (ruleIds: number[]) => void;
  /** Where this job's models come from when it has no rules of its own
   *  ("DEFAULT"), so a model allowed only that way can be pinned here. */
  inheritedFrom?: string | null;
}

const TONE: Record<string, string> = {
  switch: 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20',
  mixed: 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20',
  on_pick: 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20',
};


// Shorter headers for the columns that decide, and badges that read as a word.
const HEADER: Record<string, string> = { on_time: 'on time', seconds: 'speed', tokens: 'tokens', harm: 'harm' };
const BADGE: Record<string, string> = {
  Tokens: 'cheapest',
  'Output tokens': 'least output',
  Speed: 'fastest',
  Calls: 'fewest calls',
  Harm: 'least harm',
  'On time': 'always on time',
};

function mainCell(name: string, r: BenchAdvice['rows'][number]): string {
  if (name === 'harm') return String(r.harm);
  if (name === 'seconds') return `${r.seconds.toFixed(0)}s`;
  if (name === 'tokens') return formatMetric('tokens', r.tokens);
  return formatMetric(name, r.metrics[name]);
}

function cellTone(name: string, r: BenchAdvice['rows'][number]): string {
  if (name === 'harm' && r.harm > 0) return 'text-red-700 dark:text-red-300';
  if (name === 'on_time' && (r.metrics.on_time ?? 1) < 0.5) return 'text-red-700 dark:text-red-300';
  return '';
}

/** Allow / Remove on a recommended model — the rows tied for best and those
 * that lead a metric ("best at" badges), since those are the models the bench
 * is actually putting forward. A row with neither is not worth a button. */
function RowAction({
  model,
  allowed,
  rules,
  busy,
  inheritedFrom,
  onAllow,
  onRemove,
}: {
  model: string;
  allowed: boolean;
  rules: StageModelRule[];
  busy: boolean;
  /** The rung this job's models come from today, when it has no rules here. */
  inheritedFrom?: string | null;
  onAllow: (model: string) => void;
  onRemove: (ruleIds: number[]) => void;
}) {
  const action = rowRuleAction(model, allowed, rules, inheritedFrom ?? null);
  if (action.kind === 'pin') {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => onAllow(model)}
        data-testid={`pin-${model}`}
        className="px-2 py-0.5 border border-blue-600 text-blue-700 dark:text-blue-300 rounded text-[11px] disabled:opacity-50"
        title={`Allow ${model} here by name. It runs today because ${action.from ?? 'another rule'} allows it; a rule of its own keeps it when that changes.`}
      >
        Allow
      </button>
    );
  }
  if (action.kind === 'allow') {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => onAllow(model)}
        className="px-2 py-0.5 border border-blue-600 text-blue-700 dark:text-blue-300 rounded text-[11px] disabled:opacity-50"
        title={`Allow ${model} on this job`}
      >
        Allow
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        if (
          action.lastRule &&
          !window.confirm(
            `${model} is the only model this job allows. Removing it makes the job follow its group or DEFAULT again. Remove it?`,
          )
        )
          return;
        onRemove(action.ruleIds);
      }}
      className="px-2 py-0.5 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-[11px] disabled:opacity-50"
      title={`Remove the rule allowing ${model} on this job`}
    >
      Remove
    </button>
  );
}

export default function BenchAdvicePanel({
  advice,
  readOnly,
  busy,
  onAllow,
  onUseOnly,
  rules,
  onRemove,
  inheritedFrom = null,
}: Props) {
  const [showMore, setShowMore] = useState(false);
  const headline = adviceHeadline(advice);
  if (!advice || !headline) return null;
  const pick = advice.pick;
  const actionable = !!pick && (advice.status === 'switch' || advice.status === 'mixed');
  // ALREADY RUNNING THE PICK, BUT ONLY BY INHERITANCE. There was nothing to
  // click: the job kept no rule of its own, so the bench's answer lived on
  // another job's rule and would leave with it.
  const pinnable =
    !!pick &&
    !actionable &&
    rowRuleAction(pick, advice.rows.find((r) => r.model === pick)?.allowed ?? false, rules, inheritedFrom).kind ===
      'pin';
  // Every metric column this job reports, labelled from the bench's registry.
  const columns = advice.columns
    .map((name) => advice.metric_defs.find((d) => d.name === name))
    .filter((d): d is BenchMetricDef => Boolean(d));
  const leads = new Set(advice.standouts.flatMap((s) => s.leaders.map((m) => `${s.metric}|${m}`)));
  const shone = standoutsByModel(advice.standouts.filter((s) => s.metric !== 'clean_rate'));
  const summary = adviceSummary(advice);
  const split = splitAdviceColumns(advice);
  const def = (name: string) => advice.metric_defs.find((d) => d.name === name);
  const mainCols = split.main.map(def).filter((d): d is BenchMetricDef => Boolean(d));
  const extraCols = split.extra.map(def).filter((d): d is BenchMetricDef => Boolean(d));
  const saturated = split.saturated;

  return (
    <div
      data-testid="bench-advice"
      className={`rounded-md border p-3 text-xs ${TONE[advice.status] ?? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-medium text-gray-800 dark:text-gray-100">{headline}</p>
        {pinnable && !readOnly && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onAllow(pick)}
            data-testid="pin-pick"
            className="shrink-0 px-2 py-1 border border-blue-600 text-blue-700 dark:text-blue-300 rounded disabled:opacity-50"
            title="It runs here today through another rule - inherited, or matched by a pattern. Pinning it by name keeps this job on the model you benched when that rule changes."
          >
            Allow <span className="font-mono">{pick}</span> here
          </button>
        )}
        {actionable && !readOnly && (
          <div className="flex shrink-0 gap-2">
            {advice.status === 'switch' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onAllow(pick)}
                className="px-2 py-1 border border-blue-600 text-blue-700 dark:text-blue-300 rounded disabled:opacity-50"
                title="Add it beside the models already allowed; the pool rotates across all of them."
              >
                Allow too
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => onUseOnly(pick)}
              className="px-2 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
              title="Allow it and remove every other rule on this job (and seat)."
            >
              Use only <span className="font-mono">{pick}</span>
            </button>
          </div>
        )}
      </div>
      {actionable && readOnly && (
        <p className="mt-1 text-gray-600 dark:text-gray-300">This policy is read-only - make a draft to apply it.</p>
      )}

      {summary.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-gray-700 dark:text-gray-200" data-testid="bench-summary">
          {summary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}

      <div className="mt-2 overflow-x-auto">
        <table className="w-full">
          <thead className="text-left text-gray-500 dark:text-gray-400">
            <tr>
              <th className="font-normal py-1">model</th>
              <th className="font-normal py-1 pl-2 text-right" title="Runs that did the whole job with nothing harmful, out of all runs">
                clean
              </th>
              {mainCols.map((c) => (
                <th key={c.name} className="font-normal py-1 pl-3 text-right whitespace-nowrap" title={`${c.description} (${c.better} is better)`}>
                  {HEADER[c.name] ?? c.label}
                </th>
              ))}
              {showMore &&
                extraCols.map((c) => (
                  <th key={c.name} className="font-normal py-1 pl-3 text-right whitespace-nowrap" title={`${c.description} (${c.better} is better)`}>
                    {c.label}
                  </th>
                ))}
              <th className="font-normal py-1 pl-3">best at</th>
              {!readOnly && <th className="font-normal py-1 pl-3" />}
            </tr>
          </thead>
          <tbody>
            {advice.rows.map((r) => {
              const rate = r.n ? r.clean_runs / r.n : 0;
              const badges = shone.get(r.model) ?? [];
              return (
                <tr
                  key={r.model}
                  className={`border-t border-gray-200 dark:border-gray-700 ${r.tied ? '' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  <td className="py-1 font-mono whitespace-nowrap">
                    <span className="inline-block w-3">{r.pick ? '→' : r.tied ? '=' : ''}</span>
                    {r.model}
                    {r.allowed && <span className="ml-1 font-sans text-emerald-700 dark:text-emerald-300">· allowed</span>}
                    {!r.in_pool && (
                      <span className="ml-1 font-sans text-amber-700 dark:text-amber-300" title="No shared, enabled provider carries it.">
                        · not in pool
                      </span>
                    )}
                    {(r.providers?.length ?? 0) > 0 && (
                      <div
                        className={`pl-3 text-[10px] font-sans ${
                          (r.providers?.length ?? 0) > 1 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'
                        }`}
                        title={(r.providers?.length ?? 0) > 1 ? 'Measured across more than one provider - not one measurement.' : undefined}
                      >
                        on {r.providers!.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="py-1 pl-2 text-right font-mono whitespace-nowrap">
                    {r.clean_runs}/{r.n} <span className="text-gray-500 dark:text-gray-400">· {Math.round(rate * 100)}%</span>
                  </td>
                  {mainCols.map((c) => (
                    <td
                      key={c.name}
                      className={`py-1 pl-3 text-right font-mono whitespace-nowrap ${cellTone(c.name, r)} ${
                        leads.has(`${c.name}|${r.model}`) ? 'font-bold' : ''
                      }`}
                    >
                      {mainCell(c.name, r)}
                    </td>
                  ))}
                  {showMore &&
                    extraCols.map((c) => (
                      <td
                        key={c.name}
                        className={`py-1 pl-3 text-right font-mono ${leads.has(`${c.name}|${r.model}`) ? 'font-bold text-emerald-700 dark:text-emerald-300' : ''}`}
                      >
                        {formatMetric(c.name, r.metrics[c.name])}
                      </td>
                    ))}
                  <td className="py-1 pl-3">
                    <div className="flex flex-wrap gap-1">
                      {badges.map((b) => (
                        <span
                          key={b}
                          className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 text-[10px] whitespace-nowrap"
                        >
                          {BADGE[b] ?? b}
                        </span>
                      ))}
                    </div>
                  </td>
                  {!readOnly && (
                    <td className="py-1 pl-3 text-right whitespace-nowrap">
                      {/* THE ROWS THE BENCH IS RECOMMENDING: tied for best, or
                          best at something (cheapest, fastest, least harm).
                          Gating on `tied` alone left the model already running
                          — cheapest and fastest here — with nothing to click;
                          a button on all fourteen made allowing a 0/15 model
                          as easy as allowing the pick. */}
                      {(r.tied || badges.length > 0) && (
                        <RowAction
                          model={r.model}
                          allowed={r.allowed}
                          rules={rules}
                          busy={busy}
                          inheritedFrom={inheritedFrom}
                          onAllow={onAllow}
                          onRemove={onRemove}
                        />
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500 dark:text-gray-400">
        {extraCols.length > 0 && (
          <button type="button" onClick={() => setShowMore((v) => !v)} className="text-blue-600 dark:text-blue-400 hover:underline">
            {showMore ? 'Fewer metrics' : `More metrics (${extraCols.map((c) => c.label).join(', ')})`}
          </button>
        )}
        {saturated.length > 0 && <span>Every model scored 95%+ on {saturated.join(', ')} - not shown.</span>}
      </div>

      <p className="mt-2 text-gray-500 dark:text-gray-400">
        → the pick, the cheapest of the models tied for best · = tied (within 2 runs and 20 points) · harm counts damaging acts across all runs.
        {' '}Bench: {advice.fixtures.join(', ')}
        {advice.measured ? ` · measured ${advice.measured}` : ''}.
        {advice.unmeasured_patterns.length > 0 &&
          ` Also allowed but never benched: ${advice.unmeasured_patterns.join(', ')}.`}
      </p>
    </div>
  );
}
